import { useState, useEffect } from 'react';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useFeedback } from '../hooks/useFeedback';
import { FEEDBACK_TOPICS } from '../types/feedback.types';
import type { FeedbackTopic } from '../types/feedback.types';

interface Props {
  isOpen: boolean;
  conversationId: string | null;
  onClose: () => void;
}

export const FeedbackModal = ({ isOpen, conversationId, onClose }: Props) => {
  const { user, appUser } = useAuth();
  const { isSubmitting, submitError, isSuccess, submitFeedback, reset } = useFeedback();

  const [topic, setTopic] = useState<FeedbackTopic>(FEEDBACK_TOPICS[0]);
  const [message, setMessage] = useState('');

  const userName = appUser?.name ?? user?.name ?? '';
  const userEmail = appUser?.email ?? user?.email ?? '';

  // Auto-close after success
  useEffect(() => {
    if (!isSuccess) return;
    const timer = setTimeout(() => {
      handleClose();
    }, 1500);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    setMessage('');
    setTopic(FEEDBACK_TOPICS[0]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conversationId || !message.trim()) return;
    await submitFeedback(conversationId, topic, message);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {/* Read-only context fields */}
          <div className="flex flex-col gap-3 rounded-md border border-slate-100 bg-slate-50 px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-slate-500">Conversation ID</span>
              <span className="text-sm text-slate-700 font-mono truncate">{conversationId}</span>
            </div>
            <div className="flex gap-4">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <span className="text-xs font-medium text-slate-500">Name</span>
                <span className="text-sm text-slate-700 truncate">{userName}</span>
              </div>
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <span className="text-xs font-medium text-slate-500">Email</span>
                <span className="text-sm text-slate-700 truncate">{userEmail}</span>
              </div>
            </div>
          </div>

          {/* Topic selector */}
          <div className="flex flex-col gap-1">
            <label htmlFor="feedback-topic" className="text-sm font-medium text-slate-700">
              Topic <span className="text-red-500">*</span>
            </label>
            <select
              id="feedback-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value as FeedbackTopic)}
              disabled={isSubmitting || isSuccess}
              className="rounded-md border border-slate-300 px-3 py-2 text-base outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 transition bg-white disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            >
              {FEEDBACK_TOPICS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Message textarea */}
          <div className="flex flex-col gap-1">
            <label htmlFor="feedback-message" className="text-sm font-medium text-slate-700">
              Details <span className="text-red-500">*</span>
            </label>
            <textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe the issue in detail..."
              rows={4}
              disabled={isSubmitting || isSuccess}
              className="rounded-md border border-slate-300 px-3 py-2 text-base outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 transition resize-none disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed"
            />
          </div>

          {/* Error banner */}
          {submitError && (
            <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{submitError}</p>
          )}

          {/* Success banner */}
          {isSuccess && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-md px-3 py-2">
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>Your feedback has been submitted. Thank you!</span>
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !message.trim() || isSuccess}
            >
              {isSubmitting ? 'Sending…' : 'Send feedback'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
